import React, { useState, useRef } from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  VStack,
  useDisclosure,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  IconButton,
  HStack,
  Image,
  Icon,
  InputGroup,
  InputRightElement,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaWrench } from 'react-icons/fa';
import { useCreateMaintenanceLog, useGetVehicleMaintenanceLogs, useDeleteMaintenanceLog, useUpdateMaintenanceLog, useProcessServiceInvoice } from '../../utils/maintenance.api';
import uploadIcon from '../../Assets/icons/uploadDocumentIconBlue.svg';

const MAIN_SERVICE_TYPES = [
  "Standard Maintenance",
  "Advanced Maintenance",
  "Warranty Repair",
  "Problem Repair",
  "Customization",
  "Other",
];

const SUB_SERVICE_TYPES_BY_MAIN = {
  "Standard Maintenance": [
    "Oil and oil filter change",
    "Cabin air filter change",
    "Engine air filter change",
    "Tire Rotation",
    "Headlight replacement",
    "Bulb replacement",
    "Windshield wiper replacement front",
    "Windshield wiper replacement rear (if applicable)",
    "Key fob battery replacement",
  ],
  "Advanced Maintenance": [
    "Brake fluid change",
    "Front brake pads/shoes replaced",
    "Front brake rotors/drums replaced",
    "Rear brake pads/shoes replaced",
    "Rear brake rotors/drums replaced",
    "Tire balancing",
    "Tire replacement (2 front)",
    "Tire replacement (2 rear)",
    "Tire replacement (all 4 tires)",
    "Battery replacement",
    "Suspension alignment (front)",
    "Suspension alignment (complete)",
    "Transmission service",
    "Timing belt or chain replacement",
    "Engine coolant change",
    "Axle fluid change",
    "Spark plug replacement",
  ],
};

const createInitialFormState = () => ({
  description: '',
  price: '',
  date: '',
  mileage: '',
  mainServiceTypes: [],
  subServiceTypesByMain: {},
});

const normalizeToArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const initializeSubServiceMap = (selectedMainTypes, previousMap = {}) => {
  const map = {};
  selectedMainTypes.forEach((mainType) => {
    const allowedSubServices = SUB_SERVICE_TYPES_BY_MAIN[mainType];
    const previousSelections = previousMap[mainType] || [];
    if (allowedSubServices) {
      map[mainType] = previousSelections.filter((sub) => allowedSubServices.includes(sub));
    } else {
      map[mainType] = [...previousSelections];
    }
  });
  return map;
};

const flattenSubServiceSelections = (subServiceMap) => {
  if (!subServiceMap) return [];
  const selections = Object.values(subServiceMap).flat();
  return Array.from(new Set(selections));
};

const buildSubServiceMapFromExisting = (mainTypes, subServiceList) => {
  const map = initializeSubServiceMap(mainTypes);
  if (!subServiceList.length || !mainTypes.length) {
    return map;
  }

  const assignedSubs = new Set();

  mainTypes.forEach((mainType) => {
    const allowedSubServices = SUB_SERVICE_TYPES_BY_MAIN[mainType];
    if (allowedSubServices?.length) {
      const matchedSubs = subServiceList.filter((sub) => allowedSubServices.includes(sub));
      if (matchedSubs.length) {
        map[mainType] = matchedSubs;
        matchedSubs.forEach((sub) => assignedSubs.add(sub));
      }
    }
  });

  const unassignedSubs = subServiceList.filter((sub) => !assignedSubs.has(sub));
  if (unassignedSubs.length) {
    const fallbackMainType = mainTypes[0];
    if (fallbackMainType) {
      map[fallbackMainType] = [...(map[fallbackMainType] || []), ...unassignedSubs];
    }
  }

  return map;
};

const MaintenanceLogsCard = ({ vehicleId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure();
  const bg = useColorModeValue('white', 'gray.800');
  const toast = useToast();
  
  // State for selected record for delete/update
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Image upload state
  const fileInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // API hooks
  const { data: maintenanceLogs = [], isLoading, error } = useGetVehicleMaintenanceLogs(vehicleId);
  const { mutate: createMaintenanceLog, isLoading: isCreating } = useCreateMaintenanceLog();
  const { mutate: deleteMaintenanceLog, isLoading: isDeleting } = useDeleteMaintenanceLog(vehicleId);
  const { mutate: updateMaintenanceLog, isLoading: isUpdating } = useUpdateMaintenanceLog(vehicleId);
  const processServiceInvoiceMutation = useProcessServiceInvoice();
  
  // Form state
  const [formData, setFormData] = useState(createInitialFormState());

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMainServiceSelection = (selectedValues) => {
    const normalizedSelection = Array.isArray(selectedValues) ? selectedValues : [selectedValues].filter(Boolean);
    const uniqueSelection = Array.from(new Set(normalizedSelection));
    setFormData((prev) => ({
      ...prev,
      mainServiceTypes: uniqueSelection,
      subServiceTypesByMain: initializeSubServiceMap(uniqueSelection, prev.subServiceTypesByMain),
    }));
  };

  const handleSubServiceSelection = (mainType, selectedSubServices) => {
    const normalizedSelection = Array.isArray(selectedSubServices)
      ? selectedSubServices
      : [selectedSubServices].filter(Boolean);
    const uniqueSelection = Array.from(new Set(normalizedSelection));
    setFormData((prev) => ({
      ...prev,
      subServiceTypesByMain: {
        ...prev.subServiceTypesByMain,
        [mainType]: uniqueSelection,
      },
    }));
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear image
  const clearImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Process invoice image
  const handleProcessInvoice = async () => {
    if (!uploadedImage) {
      toast({
        title: "No Image",
        description: "Please upload an image first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!vehicleId) {
      toast({
        title: "Error",
        description: "Vehicle ID is required to process invoice.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await processServiceInvoiceMutation.mutateAsync({
        invoiceFile: uploadedImage,
        vehicleId: vehicleId,
      });
      
      console.log('Service Invoice Processing Response:', response);
      
      // Auto-populate form fields with API response data
      if (response) {
        setFormData((prev) => {
          const updated = {
            ...prev,
            subServiceTypesByMain: { ...prev.subServiceTypesByMain },
          };

          if (response.service_date) {
            const dateValue = response.service_date;
            if (typeof dateValue === 'string' && dateValue.includes('T')) {
              updated.date = dateValue.split('T')[0];
            } else if (typeof dateValue === 'string') {
              updated.date = dateValue;
            } else {
              const date = new Date(dateValue);
              updated.date = date.toISOString().split('T')[0];
            }
          }

          if (response.mileage !== null && response.mileage !== undefined) {
            updated.mileage = response.mileage.toString();
          }

          if (response.service_type) {
            const serviceTypes = normalizeToArray(response.service_type);
            updated.mainServiceTypes = serviceTypes;
            updated.subServiceTypesByMain = initializeSubServiceMap(serviceTypes);
          }

          if (response.notes) {
            updated.description = response.notes;
          }

          if (response.price !== null && response.price !== undefined) {
            updated.price = response.price.toString();
          }

          return updated;
        });
      }
      
      toast({
        title: "Success",
        description: "Invoice processed successfully! Form fields have been auto-filled.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to process invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process invoice. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add form validation: Mileage is required
  const isAddFormValid = () => {
    return !!(formData.mileage?.trim() && vehicleId);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that mileage is provided and vehicleId exists
    if (!isAddFormValid()) {
      toast({
        title: 'Validation Error',
        description: 'Mileage is required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!vehicleId) {
      toast({
        title: 'Validation Error',
        description: 'Vehicle ID is required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Map form data to API payload - only include fields that have values
    const payload = {
      vehicle_id: vehicleId,
    };

    if (formData.date) {
      payload.service_date = formData.date;
    }
    
    if (formData.price?.trim()) {
      payload.price = parseFloat(formData.price);
    }
    
    if (formData.mileage?.trim()) {
      payload.mileage = parseInt(formData.mileage, 10);
    }
    
    if (formData.mainServiceTypes?.length) {
      payload.service_type = formData.mainServiceTypes.join(', ');
    }
    const selectedSubServices = flattenSubServiceSelections(formData.subServiceTypesByMain);
    if (selectedSubServices.length) {
      payload.sub_service_type = selectedSubServices.join(', ');
    }
    
    if (formData.description?.trim()) {
      payload.notes = formData.description;
    }

    createMaintenanceLog(payload, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Maintenance log created successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Reset form and close modal
        setFormData(createInitialFormState());
        clearImage();
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message || 'Failed to create maintenance log.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format price for display
  const formatPrice = (price) => {
    if (!price) return '-';
    return `$${parseFloat(price).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Transform API response to table format
  const maintenanceRecords = maintenanceLogs.map((log) => {
    const maintenanceTypeArray = normalizeToArray(log.service_type);
    const subServiceTypeArray = normalizeToArray(log.sub_service_type);

    return {
      id: log.id,
      description: log.notes || '-',
      price: log.price,
      date: log.service_date,
      mileage: log.mileage,
      maintenanceType: maintenanceTypeArray,
      subServiceType: subServiceTypeArray,
      originalLog: log, // Keep original log data for update
    };
  });

  // Handle delete button click
  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    onDeleteOpen();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (!selectedRecord) return;

    deleteMaintenanceLog(selectedRecord.id, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Maintenance log deleted successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setSelectedRecord(null);
        onDeleteClose();
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete maintenance log.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  // Handle update button click
  const handleUpdateClick = (record) => {
    setSelectedRecord(record);
    // Populate form with existing data
    const mainServiceTypes = normalizeToArray(record.originalLog.service_type);
    const subServiceList = normalizeToArray(record.originalLog.sub_service_type);
    const priceValue = record.originalLog.price;
    const mileageValue = record.originalLog.mileage;
    setFormData({
      description: record.originalLog.notes || '',
      price: priceValue !== null && priceValue !== undefined ? priceValue.toString() : '',
      date: record.originalLog.service_date || '',
      mileage: mileageValue !== null && mileageValue !== undefined ? mileageValue.toString() : '',
      mainServiceTypes,
      subServiceTypesByMain: buildSubServiceMapFromExisting(mainServiceTypes, subServiceList),
    });
    onUpdateOpen();
  };

  // Handle update form submission
  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedRecord) return;
    
    // Validate form
    if (
      !formData.description ||
      !formData.price ||
      !formData.date ||
      !formData.mileage ||
      !formData.mainServiceTypes?.length ||
      !vehicleId
    ) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Map form data to API payload (same as create, but send all fields)
    const payload = {
      notes: formData.description,
      service_date: formData.date,
      price: formData.price,
      mileage: parseInt(formData.mileage, 10),
      service_type: formData.mainServiceTypes.join(', '),
    };
    const selectedSubServices = flattenSubServiceSelections(formData.subServiceTypesByMain);
    if (selectedSubServices.length) {
      payload.sub_service_type = selectedSubServices.join(', ');
    }

    updateMaintenanceLog(
      {
        logId: selectedRecord.id,
        updateData: payload,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Maintenance log updated successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          // Reset form and close modal
          setSelectedRecord(null);
          setFormData(createInitialFormState());
          onUpdateClose();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to update maintenance log.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };

  // Handle update form close
  const handleUpdateClose = () => {
    setSelectedRecord(null);
    setFormData(createInitialFormState());
    onUpdateClose();
  };

  // Handle add modal close
  const handleAddModalClose = () => {
    setFormData(createInitialFormState());
    clearImage();
    onClose();
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        overflow="hidden"
        bg={bg}
        boxShadow="sm"
      >
        <Flex
          justify="space-between"
          align="center"
          bg="#212e89"
          color="white"
          px={4}
          py={2}
        >
          <Flex align="center" gap={2}>
            <Heading size="lg" fontWeight="semibold">
              Maintenance Logs
            </Heading>
            <Icon as={FaWrench} boxSize={5} />
          </Flex>
        </Flex>
        <Box p={6} display="flex" justifyContent="center" alignItems="center" minH="200px">
          <Spinner size="xl" color="blue.500" />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        overflow="hidden"
        bg={bg}
        boxShadow="sm"
      >
        <Flex
          justify="space-between"
          align="center"
          bg="#212e89"
          color="white"
          px={4}
          py={2}
        >
          <Flex align="center" gap={2}>
            <Heading size="lg" fontWeight="semibold">
              Maintenance Logs
            </Heading>
            <Icon as={FaWrench} boxSize={5} />
          </Flex>
        </Flex>
        <Box p={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>Failed to load maintenance logs: {error.message}</Text>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        overflow="hidden"
        bg={bg}
        boxShadow="sm"
      >
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          bg="#212e89"
          color="white"
          px={4}
          py={2}
        >
          <Flex align="center" gap={4}>
            <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
              Maintenance Logs
            </Heading>
            <Icon as={FaWrench} boxSize={7} />
          </Flex>
        </Flex>

        <Box p={6}>
          {/* Add New Record Button */}
          <Flex justify="flex-end" mb={4}>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={() => {
                // Reset form when opening add modal
                setFormData(createInitialFormState());
                clearImage();
                setSelectedRecord(null);
                onOpen();
              }}
              _hover={{ bg: 'blue.600' }}
              isDisabled={!vehicleId}
            >
              Add New Record
            </Button>
          </Flex>

          {/* Table */}
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr bg="gray.100">
                  <Th color="gray.700" fontWeight="bold" fontSize="sm">
                    Description
                  </Th>
                  <Th color="gray.700" fontWeight="bold" fontSize="sm" isNumeric>
                    Price
                  </Th>
                  <Th color="gray.700" fontWeight="bold" fontSize="sm">
                    Date
                  </Th>
                  <Th color="gray.700" fontWeight="bold" fontSize="sm" isNumeric>
                    Mileage
                  </Th>
                  <Th color="gray.700" fontWeight="bold" fontSize="sm">
                    Maintenance Type
                  </Th>
                  <Th color="gray.700" fontWeight="bold" fontSize="sm" textAlign="center">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {maintenanceRecords.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                      <Text>No maintenance records yet. Click "Add New Record" to get started.</Text>
                    </Td>
                  </Tr>
                ) : (
                  maintenanceRecords.map((record) => (
                    <Tr key={record.id} _hover={{ bg: 'gray.50' }}>
                      <Td>{record.description}</Td>
                      <Td isNumeric fontWeight="semibold" color="green.600">
                        {formatPrice(record.price)}
                      </Td>
                      <Td>{formatDate(record.date)}</Td>
                      <Td isNumeric>{record.mileage ? `${parseInt(record.mileage).toLocaleString()} mi` : '-'}</Td>
                      <Td>
                        {record.maintenanceType.length ? record.maintenanceType.join(', ') : '-'}
                        {record.subServiceType.length ? ` \u2192 ${record.subServiceType.join(', ')}` : ''}
                      </Td>
                      <Td>
                        <HStack spacing={2} justify="center">
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            aria-label="Update record"
                            onClick={() => handleUpdateClick(record)}
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            aria-label="Delete record"
                            onClick={() => handleDeleteClick(record)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Add Record Modal */}
      <Modal isOpen={isOpen} onClose={handleAddModalClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="md">
          <ModalHeader bg="#212e89" color="white" borderTopRadius="md">
            Add New Maintenance Record
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <form onSubmit={handleSubmit}>
            <ModalBody pt={6}>
              <VStack spacing={4}>
                {/* Image Upload Section */}
                <VStack spacing={2} align="stretch" w="full">
                  <HStack spacing={3}>
                    <Image src={uploadIcon} alt="Upload" w="4" h="4" />
                    <Text fontSize="md" fontWeight="bold" color="black">
                      Upload Invoice Image
                    </Text>
                  </HStack>
                  <Box position="relative">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      ref={fileInputRef}
                      position="absolute"
                      opacity={0}
                      width="100%"
                      height="100%"
                      cursor="pointer"
                      zIndex={2}
                    />
                    <Box
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                      cursor="pointer"
                      _hover={{
                        borderColor: "blue.400",
                        backgroundColor: "blue.50"
                      }}
                      transition="all 0.2s"
                      position="relative"
                      minH="150px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {imagePreview ? (
                        <VStack spacing={2} w="full" h="full">
                          <Box w="full" h="full" borderRadius="md" overflow="hidden" flex="1">
                            <Image 
                              src={imagePreview} 
                              alt="Uploaded Preview" 
                              w="full" 
                              h="full" 
                              maxH="120px" 
                              objectFit="contain" 
                            />
                          </Box>
                          <VStack spacing={1}>
                            <Text fontSize="sm" fontWeight="small" color="gray.700">
                              {uploadedImage.name}
                            </Text>
                          </VStack>
                        </VStack>
                      ) : (
                        <VStack spacing={3}>
                          <Image 
                            src={uploadIcon} 
                            alt="Upload" 
                            w="8" 
                            h="8" 
                            opacity={0.7}
                          />
                          <VStack spacing={1}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                              Click to upload or drag
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              PNG, JPG, GIF up to 10MB
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                    </Box>
                    {uploadedImage && (
                      <IconButton
                        aria-label="Clear image"
                        icon={<Text fontSize="xs" fontWeight="bold">×</Text>}
                        onClick={clearImage}
                        variant="ghost"
                        size="sm"
                        colorScheme="red"
                        borderRadius="full"
                        position="absolute"
                        top={2}
                        right={2}
                        zIndex={3}
                        _hover={{ bg: "red.100" }}
                      />
                    )}
                  </Box>
                  {uploadedImage && (
                    <Button
                      colorScheme="blue"
                      w="full"
                      h="35px"
                      borderRadius="8px"
                      fontSize="sm"
                      onClick={handleProcessInvoice}
                      isLoading={processServiceInvoiceMutation.isLoading}
                      loadingText="Processing..."
                      disabled={processServiceInvoiceMutation.isLoading}
                      leftIcon={<Image src={uploadIcon} alt="Process" w="4" h="4" />}
                    >
                      {processServiceInvoiceMutation.isLoading ? <Spinner size="xs" /> : "Process Invoice Image"}
                    </Button>
                  )}
                </VStack>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Oil change, Tire rotation"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Price ($)</FormLabel>
                  <Input
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date</FormLabel>
                  <Input
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    focusBorderColor="blue.500"
                    max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Mileage</FormLabel>
                  <Input
                    name="mileage"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.mileage}
                    onChange={handleInputChange}
                    placeholder="e.g., 50000"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Main Service Types</FormLabel>
                  <CheckboxGroup
                    colorScheme="blue"
                    value={formData.mainServiceTypes}
                    onChange={handleMainServiceSelection}
                  >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {MAIN_SERVICE_TYPES.map((type) => (
                        <Checkbox key={type} value={type}>
                          {type}
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                </FormControl>

                {formData.mainServiceTypes.map((mainType) => {
                  const subServiceOptions = SUB_SERVICE_TYPES_BY_MAIN[mainType];
                  if (!subServiceOptions) {
                    return null;
                  }
                  return (
                    <FormControl key={mainType}>
                      <FormLabel>Sub Service Types ({mainType})</FormLabel>
                      <CheckboxGroup
                        colorScheme="blue"
                        value={formData.subServiceTypesByMain[mainType] || []}
                        onChange={(values) => handleSubServiceSelection(mainType, values)}
                      >
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                          {subServiceOptions.map((type) => (
                            <Checkbox key={type} value={type}>
                              {type}
                            </Checkbox>
                          ))}
                        </SimpleGrid>
                      </CheckboxGroup>
                    </FormControl>
                  );
                })}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleAddModalClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isDisabled={!isAddFormValid() || isCreating || !vehicleId}
                isLoading={isCreating}
                loadingText="Adding..."
              >
                Add Record
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="md">
          <ModalHeader bg="red.500" color="white" borderTopRadius="md">
            Delete Maintenance Log
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <ModalBody pt={6}>
            <Text fontSize="md" mb={4}>
              Are you sure you want to delete this maintenance log?
            </Text>
            {selectedRecord && (
              <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>Record Details:</Text>
                <Text fontSize="sm"><strong>Description:</strong> {selectedRecord.description}</Text>
                <Text fontSize="sm"><strong>Date:</strong> {formatDate(selectedRecord.date)}</Text>
                <Text fontSize="sm">
                  <strong>Type:</strong>{' '}
                  {selectedRecord.maintenanceType?.length ? selectedRecord.maintenanceType.join(', ') : '-'}
                </Text>
                {selectedRecord.subServiceType?.length ? (
                  <Text fontSize="sm">
                    <strong>Sub Type:</strong> {selectedRecord.subServiceType.join(', ')}
                  </Text>
                ) : null}
              </Box>
            )}
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">This action cannot be undone.</Text>
            </Alert>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteConfirm}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Record Modal */}
      <Modal isOpen={isUpdateOpen} onClose={handleUpdateClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="md">
          <ModalHeader bg="#212e89" color="white" borderTopRadius="md">
            Update Maintenance Record
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <form onSubmit={handleUpdateSubmit}>
            <ModalBody pt={6}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="e.g., Oil change, Tire rotation"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Price ($)</FormLabel>
                  <Input
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Date</FormLabel>
                  <Input
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    focusBorderColor="blue.500"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Mileage</FormLabel>
                  <Input
                    name="mileage"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.mileage}
                    onChange={handleInputChange}
                    placeholder="e.g., 50000"
                    focusBorderColor="blue.500"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Main Service Types</FormLabel>
                  <CheckboxGroup
                    colorScheme="blue"
                    value={formData.mainServiceTypes}
                    onChange={handleMainServiceSelection}
                  >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {MAIN_SERVICE_TYPES.map((type) => (
                        <Checkbox key={type} value={type}>
                          {type}
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                </FormControl>

                {formData.mainServiceTypes.map((mainType) => {
                  const subServiceOptions = SUB_SERVICE_TYPES_BY_MAIN[mainType];
                  if (!subServiceOptions) {
                    return null;
                  }
                  return (
                    <FormControl key={mainType}>
                      <FormLabel>Sub Service Types ({mainType})</FormLabel>
                      <CheckboxGroup
                        colorScheme="blue"
                        value={formData.subServiceTypesByMain[mainType] || []}
                        onChange={(values) => handleSubServiceSelection(mainType, values)}
                      >
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                          {subServiceOptions.map((type) => (
                            <Checkbox key={type} value={type}>
                              {type}
                            </Checkbox>
                          ))}
                        </SimpleGrid>
                      </CheckboxGroup>
                    </FormControl>
                  );
                })}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleUpdateClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isDisabled={
                  !formData.description ||
                  !formData.price ||
                  !formData.date ||
                  !formData.mileage ||
                  !formData.mainServiceTypes?.length ||
                  isUpdating ||
                  !vehicleId
                }
                isLoading={isUpdating}
                loadingText="Updating..."
              >
                Update Record
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MaintenanceLogsCard;

